import { datadogRum } from '@datadog/browser-rum';
import { v4 as uuid } from 'uuid';

export type AudioIdentity = {
  responseId?: string;
  generation?: number;
  providerConnectionEpoch?: number;
};
export type AudioFlowStatus = {
  version: 1;
  enabled: boolean;
  sessionId: string;
  provider: string | null;
  model: string | null;
  stage: string;
  inputExpected: boolean;
  gateReason: string;
  inputSampleRate: number;
  outputSampleRate: number | null;
  inputSequence: number;
  providerConnectionEpoch: number;
  config: {
    intervalMs: number;
    gapMs: number;
    energyThreshold: number;
    backlogAudioMs: number;
  };
};

const makeCounters = () => ({
  capturedBytes: 0,
  emittedBytes: 0,
  mutedBytes: 0,
  inputAttemptBytes: 0,
  inputEnqueuedBytes: 0,
  inputFailedBytes: 0,
  inputNoClientBytes: 0,
  outputReceivedBytes: 0,
  outputScheduledBytes: 0,
  outputCompletedBytes: 0,
  outputInterruptedBytes: 0,
  outputSuppressedBytes: 0,
  playbackErrors: 0,
});

export const safeAudioFlow = (callback: () => void) => {
  try {
    callback();
  } catch {
    /* Keep diagnostics outside the media control path. */
  }
};

export class BrowserAudioFlow {
  readonly browserInstanceId = uuid();
  counters = makeCounters();
  captureEpoch = 0;
  status: AudioFlowStatus | null = null;
  captureExpected = false;
  private outputSuppressed = false;
  playbackSchedulingFailed = false;
  captureState:
    | (() => {
        context: string;
        enabled: boolean;
        muted: boolean;
        ended: boolean;
        applicationMuted: boolean;
      })
    | null = null;
  playbackState:
    | (() => { context: string; pending: boolean; queueMs: number })
    | null = null;
  socketState: (() => { readyState: number; bufferedAmount: number }) | null =
    null;
  sendReport: ((report: AudioFlowReport) => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private reportSequence = 0;
  private startedAt = 0;
  private lastStatusAt = 0;
  private lastBoundaryAt = -Infinity;
  private lastCaptureAt: number | null = null;
  private captureStartedAt: number | null = null;
  private lastEnqueueAt: number | null = null;
  private lastSignalAt: number | null = null;
  private quietSince: number | null = null;
  private lastPlaybackAt: number | null = null;
  private peak = 0;
  private activeSamples = 0;
  private samples = 0;
  private outputSequence = 0;
  private firstOutput = new Map<string, number | null>();
  private disposed = false;
  private finished = false;

  constructor(
    readonly assessmentId: string,
    private now = () => performance.now(),
    private rum = (value: Record<string, unknown>) =>
      datadogRum.addAction('zara.audio_flow', { audioFlow: value }),
  ) {}

  get playbackSuppressed() {
    return this.outputSuppressed;
  }

  set playbackSuppressed(value: boolean) {
    this.outputSuppressed = value;
    if (value) this.playbackSchedulingFailed = false;
  }

  beginConnection() {
    this.flush('boundary');
    this.status = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.firstOutput.clear();
  }

  configure(status: AudioFlowStatus) {
    if (this.finished) return false;
    if (
      status?.version !== 1 ||
      !status.enabled ||
      !status.sessionId ||
      !Number.isFinite(status.config?.intervalMs) ||
      status.config.intervalMs < 5000 ||
      status.config.intervalMs > 10000
    )
      return false;
    const changed = this.status?.sessionId !== status.sessionId;
    const intervalChanged =
      this.status?.config.intervalMs !== status.config.intervalMs;
    if (changed) {
      this.counters = makeCounters();
      this.reportSequence = 0;
      this.startedAt = this.now();
      this.outputSequence = 0;
      this.playbackSchedulingFailed = false;
      this.firstOutput.clear();
      this.lastCaptureAt =
        this.lastEnqueueAt =
        this.lastSignalAt =
        this.quietSince =
        this.lastPlaybackAt =
          null;
    }
    this.status = status;
    this.lastStatusAt = this.now();
    if (this.timer && (changed || intervalChanged)) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (!this.timer && !this.disposed)
      this.timer = setInterval(
        () => this.flush('interval'),
        status.config.intervalMs,
      );
    return true;
  }

  setCaptureExpected(expected: boolean) {
    if (this.captureExpected !== expected) {
      this.flush('boundary');
      this.captureStartedAt = expected ? this.now() : null;
      this.quietSince = null;
      this.lastSignalAt = null;
    }
    this.captureExpected = expected;
  }

  capture(chunk: ArrayBuffer, muted: boolean) {
    const now = this.now();
    if (this.lastCaptureAt === null || now - this.lastCaptureAt > 1000 || muted)
      this.quietSince = null;
    this.lastCaptureAt = now;
    this.counters.capturedBytes += chunk.byteLength;
    if (muted) {
      this.counters.mutedBytes += chunk.byteLength;
      return;
    }
    if (chunk.byteLength % 2 !== 0) return;
    const samples = new Int16Array(chunk);
    let active = 0;
    for (const sample of samples) {
      const amplitude = Math.abs(sample / 32768);
      this.peak = Math.max(this.peak, amplitude);
      if (amplitude >= (this.status?.config.energyThreshold ?? 0.003)) active++;
    }
    this.samples += samples.length;
    this.activeSamples += active;
    if (active) {
      this.lastSignalAt = now;
      this.quietSince = null;
    } else if (this.quietSince === null) this.quietSince = now;
  }

  captureStarted() {
    this.captureEpoch++;
    this.captureStartedAt = this.now();
    this.lastCaptureAt = this.lastSignalAt = this.quietSince = null;
    this.flush('boundary');
  }

  inputEnqueued(bytes: number) {
    this.counters.inputEnqueuedBytes += bytes;
    this.lastEnqueueAt = this.now();
  }

  outputReceived(
    bytes: number,
    sequence: number | undefined,
    identity: AudioIdentity,
  ) {
    this.counters.outputReceivedBytes += bytes;
    if (Number.isSafeInteger(sequence) && (sequence ?? 0) > this.outputSequence)
      this.outputSequence = sequence ?? 0;
    const key = this.responseKey(identity);
    if (key && !this.firstOutput.has(key)) {
      if (this.firstOutput.size >= 32)
        this.firstOutput.delete(this.firstOutput.keys().next().value ?? '');
      this.firstOutput.set(key, this.now());
    }
    if (this.playbackSuppressed) this.counters.outputSuppressedBytes += bytes;
  }

  private responseKey(identity: AudioIdentity) {
    return (
      identity.responseId ||
      (identity.generation !== undefined
        ? `${identity.providerConnectionEpoch ?? 0}:${identity.generation}`
        : null)
    );
  }

  playbackFailed() {
    this.counters.playbackErrors++;
    this.playbackSchedulingFailed = true;
  }

  playbackProgress(identity?: AudioIdentity) {
    this.lastPlaybackAt = this.now();
    if (!identity) return;
    const key = this.responseKey(identity);
    const receivedAt = key ? this.firstOutput.get(key) : undefined;
    if (receivedAt !== undefined && receivedAt !== null) {
      safeAudioFlow(() =>
        this.rum({
          ...this.correlation(),
          kind: 'delay',
          ...identity,
          boundary: 'browser_receipt_to_playback_start',
          durationMs: this.now() - receivedAt,
          resolutionMs: 250,
        }),
      );
      if (key) this.firstOutput.set(key, null);
    }
  }

  private correlation() {
    return {
      assessmentId: this.assessmentId,
      browserInstanceId: this.browserInstanceId,
      sessionId: this.status?.sessionId,
      provider: this.status?.provider,
      model: this.status?.model,
      stage: this.status?.stage,
      gateReason: this.status?.gateReason,
      inputExpected: this.status?.inputExpected,
      inputSampleRate: this.status?.inputSampleRate,
      outputSampleRate: this.status?.outputSampleRate,
      providerConnectionEpoch: this.status?.providerConnectionEpoch,
      serverStatusAgeMs: this.now() - this.lastStatusAt,
    };
  }

  report(kind: 'interval' | 'boundary' | 'closed' = 'interval') {
    const now = this.now();
    const age = (at: number | null) =>
      at === null ? null : Math.max(0, now - at);
    const capture = this.captureState?.();
    const playback = this.playbackState?.();
    const socket = this.socketState?.();
    const context = (value: string | undefined) =>
      ['running', 'suspended', 'closed'].includes(value ?? '')
        ? (value as 'running' | 'suspended' | 'closed')
        : ('unknown' as const);
    const lastCaptureAgeMs =
      age(this.lastCaptureAt) ?? age(this.captureStartedAt);
    return {
      type: 'client.audio_flow' as const,
      version: 1 as const,
      sessionId: this.status?.sessionId ?? '',
      browserInstanceId: this.browserInstanceId,
      reportSequence: ++this.reportSequence,
      captureEpoch: this.captureEpoch,
      kind,
      elapsedMs: Math.max(0, now - this.startedAt),
      counters: { ...this.counters },
      state: {
        captureExpected: this.captureExpected,
        muted: capture?.applicationMuted ?? false,
        trackEnabled: capture?.enabled ?? false,
        trackMuted: capture?.muted ?? false,
        trackEnded: capture?.ended ?? false,
        captureContext: context(capture?.context),
        playbackContext: context(playback?.context),
        socketState: socket?.readyState ?? 3,
        bufferedWireBytes: socket?.bufferedAmount ?? 0,
        lastCaptureAgeMs,
        lastEnqueueAgeMs: age(this.lastEnqueueAt),
        lastSignalActivityAgeMs: age(this.lastSignalAt),
        consecutiveQuietMs:
          (lastCaptureAgeMs ?? Infinity) < 1000 && !capture?.applicationMuted
            ? age(this.quietSince)
            : null,
        intervalPeak: this.peak,
        activeSampleRatio: this.samples ? this.activeSamples / this.samples : 0,
        playbackPending: playback?.pending ?? false,
        playbackSuppressed: this.playbackSuppressed,
        playbackSchedulingFailed: this.playbackSchedulingFailed,
        playbackQueueMs: playback?.queueMs ?? 0,
        lastPlaybackProgressAgeMs: age(this.lastPlaybackAt),
        outputAudioSequence: this.outputSequence,
        visibility:
          document.visibilityState === 'hidden'
            ? ('hidden' as const)
            : ('visible' as const),
      },
    };
  }

  flush(kind: 'interval' | 'boundary' | 'closed') {
    if (!this.status?.enabled || this.disposed) return;
    if (kind === 'boundary' && this.now() - this.lastBoundaryAt < 1000) return;
    if (kind === 'boundary') this.lastBoundaryAt = this.now();
    safeAudioFlow(() => {
      const report = this.report(kind);
      safeAudioFlow(() => this.rum({ ...this.correlation(), ...report }));
      const bytes = new TextEncoder().encode(JSON.stringify(report)).byteLength;
      const socket = this.socketState?.();
      if (
        bytes <= 4096 &&
        socket?.readyState === 1 &&
        socket.bufferedAmount < 65536
      )
        safeAudioFlow(() => this.sendReport?.(report));
      this.peak = this.activeSamples = this.samples = 0;
    });
  }

  finish() {
    this.dispose();
    this.finished = true;
  }

  resume() {
    if (this.finished) return;
    this.disposed = false;
    if (this.status) this.configure(this.status);
  }
  dispose() {
    if (this.disposed) return;
    this.flush('closed');
    this.disposed = true;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    this.firstOutput.clear();
  }
}

export type AudioFlowReport = ReturnType<BrowserAudioFlow['report']>;

export function audioFlowRumContext(
  context: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const flow = context?.audioFlow;
  if (!flow || typeof flow !== 'object' || Array.isArray(flow)) return {};
  const result: Record<string, unknown> = {};
  const values = flow as Record<string, unknown>;
  for (const key of [
    'responseId',
    'assessmentId',
    'browserInstanceId',
    'sessionId',
    'provider',
    'model',
    'stage',
    'type',
    'kind',
    'boundary',
    'gateReason',
  ]) {
    const value = values[key];
    if (typeof value === 'string' && value.length <= 100) result[key] = value;
  }
  for (const key of [
    'generation',
    'providerConnectionEpoch',
    'version',
    'reportSequence',
    'captureEpoch',
    'elapsedMs',
    'durationMs',
    'resolutionMs',
    'inputSampleRate',
    'outputSampleRate',
    'serverStatusAgeMs',
  ]) {
    const value = values[key];
    if (typeof value === 'number' && Number.isFinite(value) && value >= 0)
      result[key] = value;
  }
  if (typeof values.inputExpected === 'boolean')
    result.inputExpected = values.inputExpected;
  const counters = values.counters;
  if (counters && typeof counters === 'object')
    result.counters = Object.fromEntries(
      Object.keys(makeCounters()).flatMap((key) => {
        const value = (counters as Record<string, unknown>)[key];
        return typeof value === 'number' && Number.isFinite(value) && value >= 0
          ? [[key, value]]
          : [];
      }),
    );
  const state = values.state;
  if (state && typeof state === 'object') {
    const source = state as Record<string, unknown>;
    const output: Record<string, unknown> = {};
    for (const key of [
      'captureExpected',
      'muted',
      'trackEnabled',
      'trackMuted',
      'trackEnded',
      'playbackPending',
      'playbackSuppressed',
      'playbackSchedulingFailed',
    ])
      if (typeof source[key] === 'boolean') output[key] = source[key];
    for (const key of ['captureContext', 'playbackContext'])
      if (
        ['running', 'suspended', 'closed', 'unknown'].includes(
          String(source[key]),
        )
      )
        output[key] = source[key];
    if (['hidden', 'visible'].includes(String(source.visibility)))
      output.visibility = source.visibility;
    for (const key of [
      'socketState',
      'bufferedWireBytes',
      'lastCaptureAgeMs',
      'lastEnqueueAgeMs',
      'lastSignalActivityAgeMs',
      'consecutiveQuietMs',
      'intervalPeak',
      'activeSampleRatio',
      'playbackQueueMs',
      'lastPlaybackProgressAgeMs',
      'outputAudioSequence',
    ]) {
      const value = source[key];
      if (
        value === null ||
        (typeof value === 'number' && Number.isFinite(value) && value >= 0)
      )
        output[key] = value;
    }
    result.state = output;
  }
  return { audioFlow: result };
}
